import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { errorMessage } from '../../api';
import { paths } from '../../app/paths';
import { Button, InlineAlert, TextField, Wordmark } from '../../ui';
import { useAuth } from './AuthProvider';

export function LoginPage() {
  const { isAuthenticated, login, sessionExpired } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = (location.state && location.state.from) || '/dashboard/projects';
  // Arriving from "Create account": say it worked and fill in the username (the password is never carried over).
  const registered: string = (location.state && location.state.registered) || '';

  const [form, setForm] = useState({ username: registered, password: '' });
  const [errors, setErrors] = useState<{ username?: string; password?: string }>({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) return <Navigate to={redirectTo} replace />;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setErrors((er) => ({ ...er, [e.target.name]: undefined }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // guards against double submit (Enter key + click)
    const next: { username?: string; password?: string } = {};
    if (!form.username.trim()) next.username = 'Enter your username.';
    if (!form.password) next.password = 'Enter your password.';
    setErrors(next);
    setFormError('');
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      const result = await login(form.username.trim(), form.password);
      if (result.ok) navigate(redirectTo, { replace: true });
      else setFormError(result.message || 'Sign-in failed.');
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="grid min-h-screen place-items-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center"><Wordmark className="text-2xl" /></div>
        <form onSubmit={onSubmit} noValidate className="rounded-card border border-line bg-surface p-6 shadow-card">
          <h1 className="text-xl font-bold tracking-tight text-ink">Sign in</h1>
          <p className="mb-5 mt-1 text-sm text-muted">Welcome back. Enter your details to continue.</p>
          <div className="space-y-4">
            {sessionExpired && !registered && <InlineAlert tone="info">Your session has ended. Sign in again to continue.</InlineAlert>}
            {registered && <InlineAlert tone="info">Account created. Sign in with your new password.</InlineAlert>}
            <TextField label="Username" name="username" autoComplete="username" autoFocus value={form.username} onChange={onChange} error={errors.username} />
            <TextField label="Password" name="password" type="password" autoComplete="current-password" value={form.password} onChange={onChange} error={errors.password} />
            {formError && <InlineAlert>{formError}</InlineAlert>}
            <Button type="submit" loading={submitting} className="w-full">Sign in</Button>
          </div>
          <p className="mt-5 text-center text-sm text-muted">New here? <Link to={paths.register()} className="font-semibold text-brand-700 hover:underline">Create account</Link></p>
        </form>
      </div>
    </main>
  );
}
